import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import styles from './MiniAllFeatures.module.scss';
import ValuePropsBox from '../../../views/ProductDetails/Series3CollarDetails/components/ValuePropsBox';
import { ReactComponent as IconStats } from '../../../assets/images/icons/icon_stats.svg';
import { ReactComponent as IconBell } from '../../../assets/images/icons/icon_bell.svg';
import { ReactComponent as IconTracking } from '../../../assets/images/icons/icon_tracking.svg';
import { ReactComponent as IconVet } from '../../../assets/images/icons/icon_vet_black.svg';
import { ReactComponent as IconCar } from '../../../assets/images/icons/icon_car.svg';
import { ReactComponent as IconGroups } from '../../../assets/images/icons/icon_groups.svg';
import { ReactComponent as IconTarget } from '../../../assets/images/icons/icon_target.svg';
import { isMobileDevice } from '../../../lib/util/isMobileDevice';
import { useHistory } from 'react-router-dom';
import AppPaths from '../../../AppPaths';
import { Species } from '../../../types/gql-op-types';
import { useSeries3Context } from '../../../contexts/Series3Context';
import * as events from '../../../lib/analytics/events';
import { getFiGrowthBook } from '../../../lib/growthbook';
import { ReactComponent as IconBarking } from '../../../assets/images/icons/icon_barking.svg';
import { ReactComponent as IconScratching } from '../../../assets/images/icons/icon_scratching.svg';
import { ReactComponent as IconEating } from '../../../assets/images/icons/icon_eating.svg';

function getFeatureProps(species: Species, isVerizon: boolean, isBehaviorEnabled: boolean, t: TFunction) {
  return [
    {
      icon: <IconTracking />,
      text: <div className={styles.valueProp}>{t('gps_lte_m_live_location_tracking')}</div>,
    },
    {
      icon: <IconBell />,
      text: <div className={styles.valueProp}>{t('escape_alerts_and_geofence')}</div>,
    },
    ...(isBehaviorEnabled && species === Species.DOG
      ? [
          {
            icon: <IconStats />,
            text: <div className={styles.valueProp}>{t('activity_and_sleep_tracking')}</div>,
          },
          {
            icon: <IconBarking />,
            text: <div className={styles.valueProp}>{t('barking_detection')}</div>,
          },
          {
            icon: <IconScratching />,
            text: <div className={styles.valueProp}>{t('scratching_and_licking_tracking')}</div>,
          },
          {
            icon: <IconEating />,
            text: <div className={styles.valueProp}>{t('eating_and_drinking_tracking')}</div>,
          },
        ]
      : [
          {
            icon: <IconStats />,
            text: (
              <div className={styles.valueProp}>
                {species === Species.DOG ? t('activity_and_sleep_tracking') : t('active_time_and_rest_tracking')}
              </div>
            ),
          },
        ]),
    ...(isVerizon
      ? [
          {
            icon: <IconTarget />,
            text: <div className={styles.valueProp}>{t('lost_pet_mode')}</div>,
          },
          {
            icon: <IconGroups />,
            text: <div className={styles.valueProp}>{t('track_your_pet_along_with_your_family')}</div>,
          },
        ]
      : []),
    ...(isVerizon
      ? []
      : [
          {
            icon: <IconVet style={{ color: 'black' }} />,
            text: <div className={styles.valueProp}>{t('smart_vet_records')}</div>,
          },
          {
            icon: <IconCar />,
            text: (
              <div className={styles.valueProp}>
                {species === Species.DOG ? t('walks_and_car_rides') : t('outings_and_car_rides')}
              </div>
            ),
          },
          {
            icon: <IconGroups />,
            text: <div className={styles.valueProp}>{t('unlimited_users')}</div>,
          },
        ]),
  ];
}

export default function MiniAllFeatures({
  species,
  containerRef,
}: {
  species: Species;
  containerRef?: React.RefObject<HTMLDivElement>;
}) {
  const { t } = useTranslation();
  const isMobile = isMobileDevice();
  const history = useHistory();
  const { isVerizon } = useSeries3Context();
  const ecomMiniBehavior = getFiGrowthBook().getFeatureValue<boolean>('ecom-mini-behavior', false);

  return (
    <div ref={containerRef} className={styles.container}>
      <h2 className={styles.heading}>{t('all_features')}</h2>
      <div className={styles.valueProps}>
        <ValuePropsBox
          className={styles.valuePropsBox}
          compact={!isMobile}
          columns={isMobile ? 1 : 2}
          valueProps={getFeatureProps(species, !!isVerizon, ecomMiniBehavior, t)}
        />
      </div>
      {!isVerizon && (
        <div
          className={styles.button}
          onClick={() => {
            events.miniCompareProductsClicked();
            history.push(AppPaths.Compare);
          }}
        >
          {t('compare_products')}
        </div>
      )}
    </div>
  );
}
